import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { StoreService } from '../../store.service';

declare const rrweb: any;

interface ClickDot {
  x: number;
  y: number;
  count: number;
  intensity: number;
}

@Component({
  selector: 'app-heatmap-view',
  templateUrl: './heatmap-view.component.html',
  styleUrls: ['./heatmap-view.component.scss'],
})
export class HeatmapViewComponent implements OnInit, AfterViewInit, OnDestroy {
  bearer: string;
  storeId: string;
  sessionId: string;

  isLoading     = true;
  error: string = null;
  session: any  = null;
  playerReady   = false;
  events: any[] = [];

  private replayer: any    = null;
  private rrwebReady       = false;
  private scale            = 1;
  private viewport         = { w: 0, h: 0 };
  private dots: ClickDot[] = [];

  private resizeTimer: any;
  private resizeHandler = () => {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this.isLoading && !this.error && this.events.length) { this.scalePlayer(); }
    }, 150);
  };

  constructor(private route: ActivatedRoute, private svc: StoreService) {}

  ngOnInit(): void {
    this.bearer    = localStorage.getItem('bearer');
    this.storeId   = localStorage.getItem('store_id');
    this.sessionId = this.route.snapshot.paramMap.get('sessionId');
    this.loadRrweb().then(() => this.loadData());
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    clearTimeout(this.resizeTimer);
    if (this.replayer) {
      try { this.replayer.pause(); } catch {}
      const root = document.getElementById('hm-player-root');
      if (root) { root.innerHTML = ''; }
      this.replayer = null;
    }
  }

  // ── Load rrweb from CDN ──────────────────────────────────────────────────

  private loadRrweb(): Promise<void> {
    if (typeof rrweb !== 'undefined') { this.rrwebReady = true; return Promise.resolve(); }
    return new Promise(resolve => {
      const css  = document.createElement('link');
      css.rel    = 'stylesheet';
      css.href   = 'https://cdn.jsdelivr.net/npm/rrweb@1.1.3/dist/rrweb.min.css';
      document.head.appendChild(css);

      const s   = document.createElement('script');
      s.src     = 'https://cdn.jsdelivr.net/npm/rrweb@1.1.3/dist/rrweb.min.js';
      s.onload  = () => { this.rrwebReady = true; resolve(); };
      s.onerror = () => resolve();
      document.head.appendChild(s);
    });
  }

  // ── Fetch session meta + replay events ──────────────────────────────────

  private loadData(): void {
    this.isLoading = true;

    let analyticsRes: any = null;
    let eventsRes: any    = null;

    const tryRender = () => {
      if (analyticsRes === null || eventsRes === null) { return; }

      this.session = analyticsRes?.data ?? {};
      this.buildDots(this.session?.interactions);

      this.events    = this.patchSandboxedIframes(eventsRes?.data?.events ?? []);
      this.isLoading = false;

      if (this.events.length > 0) {
        setTimeout(() => this.initPlayer(), 0);
      } else {
        this.error = 'No snapshot data available for this session.';
      }
    };

    this.svc.getAnalyticsSession(this.bearer, this.storeId, this.sessionId).subscribe(
      res => { analyticsRes = res; tryRender(); },
      ()  => { analyticsRes = {}; tryRender(); }
    );

    this.svc.getReplayEvents(this.bearer, this.sessionId, this.storeId).subscribe(
      res => { eventsRes = res; tryRender(); },
      ()  => { eventsRes = {}; tryRender(); }
    );
  }

  // ── Build click-dot data ─────────────────────────────────────────────────

  private buildDots(interactions: any): void {
    if (!interactions?.clicks || !interactions?.viewport?.w) { return; }

    const { viewport, grid, clicks } = interactions;
    const cols        = grid?.cols          ?? 20;
    const rowHeightPx = grid?.row_height_px ?? Math.ceil(viewport.h / 40);
    const cellWidth   = viewport.w / cols;

    this.viewport = viewport;

    const counts   = Object.values(clicks) as number[];
    const maxCount = counts.length ? Math.max(...counts) : 1;

    this.dots = Object.entries(clicks).map(([key, count]: [string, number]) => {
      const [col, row] = key.split(',').map(Number);
      return {
        x:         (col + 0.5) * cellWidth,
        y:         (row + 0.5) * rowHeightPx,
        count,
        intensity: count / maxCount,
      };
    });
  }

  // ── Initialise rrweb player ──────────────────────────────────────────────

  private initPlayer(): void {
    if (!this.rrwebReady || !this.events.length) { return; }

    const root = document.getElementById('hm-player-root');
    if (!root) { return; }
    root.innerHTML = '';
    this.playerReady = false;

    this.replayer = new rrweb.Replayer(this.events, {
      root,
      speed:        16,
      skipInactive: true,
      showWarning:  false,
      showDebug:    false,
    });

    this.replayer.on('finish', () => this.expandAndDraw());

    this.scalePlayer();
    setTimeout(() => {
      try { this.replayer.play(); } catch {}
    }, 300);
  }

  // ── Scale player to fit available width ─────────────────────────────────

  private scalePlayer(attempt = 0): void {
    const root = document.getElementById('hm-player-root');
    const clip = root?.parentElement;
    if (!root || !clip) { return; }

    const card = clip.closest('.player-card') as HTMLElement;
    if (card) {
      const parent = card.parentElement;
      if (parent) { card.style.maxWidth = parent.clientWidth + 'px'; }

      const cardRect = card.getBoundingClientRect();
      const footerEl = document.querySelector('.footer') as HTMLElement;
      const footerH  = footerEl ? footerEl.offsetHeight : 70;
      const availH   = window.innerHeight - cardRect.top - 60 - footerH;
      card.style.height = Math.max(availH, 300) + 'px';
    }

    const wrapper  = root.querySelector('.replayer-wrapper') as HTMLElement;
    const naturalW = wrapper?.offsetWidth;
    const naturalH = wrapper?.offsetHeight;

    if (!naturalW || !naturalH) {
      if (attempt < 20) { setTimeout(() => this.scalePlayer(attempt + 1), 100); }
      else              { this.playerReady = true; }
      return;
    }

    if (!this.viewport.w) { this.viewport = { w: naturalW, h: naturalH }; }

    const area = clip.closest('.player-area') as HTMLElement;
    const maxW = Math.max(area ? area.clientWidth - 16 : window.innerWidth - 32, 200);
    this.scale = Math.min(1, maxW / naturalW);

    (root as any).style.zoom = this.scale < 1 ? String(this.scale) : '';
    root.style.transform       = '';
    root.style.transformOrigin = '';
    root.style.width           = '';
    root.style.height          = '';
    // playerReady stays false until expandAndDraw() completes
  }

  // ── Expand iframe to full page height then draw canvas ───────────────────
  // Called on finish — does NOT touch card layout or playerReady.

  private expandAndDraw(): void {
    const root = document.getElementById('hm-player-root');
    if (!root) { return; }

    const wrapper = root.querySelector('.replayer-wrapper') as HTMLElement;
    const iframe  = root.querySelector('iframe') as HTMLIFrameElement;

    if (iframe?.contentDocument && wrapper) {
      const fullH = iframe.contentDocument.documentElement.scrollHeight;
      if (fullH > (wrapper.offsetHeight || 0)) {
        iframe.style.height  = fullH + 'px';
        wrapper.style.height = fullH + 'px';
        this.viewport        = { w: this.viewport.w || wrapper.offsetWidth, h: fullH };
      }
      try { iframe.contentWindow.scrollTo(0, 0); } catch {}
    }

    setTimeout(() => this.drawCanvas(), 50);
  }

  // ── Draw heatmap canvas ──────────────────────────────────────────────────

  private drawCanvas(): void {
    const canvas = document.getElementById('hm-canvas') as HTMLCanvasElement;
    if (!canvas) { return; }

    const vw = this.viewport.w || 1280;
    const vh = this.viewport.h || 900;

    canvas.width        = vw;
    canvas.height       = vh;
    canvas.style.width  = Math.round(vw * this.scale) + 'px';
    canvas.style.height = Math.round(vh * this.scale) + 'px';

    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, vw, vh);
    this.playerReady = true;
    if (!this.dots.length) { return; }

    const offscreen  = document.createElement('canvas');
    offscreen.width  = vw;
    offscreen.height = vh;
    const off        = offscreen.getContext('2d')!;
    const radius     = Math.max(40, (vw / 20) * 1.5);

    for (const dot of this.dots) {
      const grad = off.createRadialGradient(dot.x, dot.y, 0, dot.x, dot.y, radius);
      grad.addColorStop(0,   `rgba(255,255,255,${(dot.intensity * 0.6).toFixed(3)})`);
      grad.addColorStop(0.4, `rgba(255,255,255,${(dot.intensity * 0.2).toFixed(3)})`);
      grad.addColorStop(1,   'rgba(255,255,255,0)');
      off.fillStyle = grad;
      off.beginPath();
      off.arc(dot.x, dot.y, radius, 0, Math.PI * 2);
      off.fill();
    }

    const imageData = off.getImageData(0, 0, vw, vh);
    const px        = imageData.data;

    for (let i = 0; i < px.length; i += 4) {
      const intensity = px[i + 3] / 255;
      if (intensity === 0) { continue; }
      const clamped   = Math.min(intensity, 1);
      const [r, g, b] = this.heatRgb(clamped);
      px[i]     = r;
      px[i + 1] = g;
      px[i + 2] = b;
      px[i + 3] = Math.floor(clamped * 210);
    }

    ctx.putImageData(imageData, 0, 0);
  }

  // rrweb faithfully recreates sandbox attributes from recorded iframes which
  // blocks script execution inside them. Patch sandbox to include allow-scripts.
  private patchSandboxedIframes(events: any[]): any[] {
    return events.map(event => {
      if (event.type === 2) {
        this.patchNode(event.data?.node);
      } else if (event.type === 3 && event.data?.source === 0) {
        (event.data.adds ?? []).forEach((add: any) => this.patchNode(add.node));
      }
      return event;
    });
  }

  private patchNode(node: any): void {
    if (!node) { return; }
    if (node.tagName === 'iframe' && node.attributes?.sandbox !== undefined) {
      const s = node.attributes.sandbox as string;
      if (!s.includes('allow-scripts')) {
        node.attributes.sandbox = s ? s + ' allow-scripts' : 'allow-scripts';
      }
    }
    (node.childNodes ?? []).forEach((child: any) => this.patchNode(child));
  }

  private heatRgb(intensity: number): [number, number, number] {
    if (intensity > 0.75) { return [255, 0,   0  ]; }
    if (intensity > 0.5)  { return [255, 100, 0  ]; }
    if (intensity > 0.25) { return [255, 220, 0  ]; }
    return                         [0,   120, 255];
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  formatDuration(ms: number): string {
    if (!ms) { return '-'; }
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    if (h > 0) {
      return `${h}h ${m % 60}m ${s % 60}s`;
    }
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }

  shortId(id: string): string {
    return id ? id.split('-')[0] : '-';
  }
}
